import React, { useCallback } from 'react'
import RoomCard from './RoomCard'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { getRoomDetails } from '@/redux/asyncThunks/room'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/redux/hooks'
import { CardType, Status } from '@/redux/types/room'
import ROUTES from '@/routes/routes'
import { NegotiationType } from '@/redux/types/negotiationWebsocket'

const NegotiationSession: React.FC = () => {
  const currentRoom = useSelector((state: RootState) => state.room.currentRoom)
  const negotiations = currentRoom?.negotiations || []
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const handleRefresh = useCallback(() => {
    if (currentRoom?.roomid) {
      dispatch(getRoomDetails(currentRoom.roomid))
    }
  }, [dispatch, currentRoom?.roomid])

  const handleJoinNegotiation = (negotiationId: string) => {
    if (currentRoom?.roomid && currentRoom?.invite_code) {
      navigate(
        ROUTES.PROFESSOR.GAME(currentRoom.roomid, {
          negotiationId: negotiationId,
          inviteCode: currentRoom.invite_code,
          panel: 'chat',
        })
      )
    }
  }

  const getDisplayStatus = (negotiation: any) => {
    if (negotiation.end_time) {
      return Status.Terminated
    }

    // If the negotiation is public
    if (negotiation.negotiation_type === NegotiationType.PUBLIC_ROOM) {
      return Status.Accepted
    }

    const members = negotiation.members || []

    // If it is a one on one
    if (members.length === 2) {
      const ownerMember = members.find(
        (member: any) => member.status === Status.Owner
      )
      const otherMember = members.find(
        (member: any) => member.status !== Status.Owner
      )

      if (ownerMember && otherMember) {
        return otherMember.status
      }
    }

    // If it is a group
    if (members.length > 2) {
      const hasAcceptedMember = members.some(
        (member: any) => member.status === Status.Accepted
      )
      if (hasAcceptedMember) {
        return Status.Accepted
      }
    }

    return negotiation.status
  }

  return (
    <div>
      <div className='flex gap-2 items-center mt-8'>
        <h5 className='text-xl font-bold text-dark-blue'>Conference Session</h5>
        <div
          onClick={handleRefresh}
          className='p-1 bg-transparent rounded-md cursor-pointer'
        >
          <img src='/icons/refresh.svg' alt='Refresh' className='size-5' />
        </div>
      </div>
      <p className='text-lg text-dark-blue'>Monitor conference session(s).</p>
      {negotiations.length > 0 ? (
        <div className='grid grid-cols-[repeat(auto-fill,_minmax(224px,_1fr))] xl:grid-cols-5 gap-9 mt-11'>
          {negotiations.map((negotiation) => {
            const displayStatus = getDisplayStatus(negotiation)

            return (
              <RoomCard
                cardType={CardType.NEGOTIATION}
                key={negotiation.id}
                roomId={negotiation.id.toString()}
                buttonText='View Conference Session'
                roomIdPrefix='Negotiation ID:'
                onButtonClick={() =>
                  handleJoinNegotiation(negotiation.id.toString())
                }
                negotiationType={negotiation.negotiation_type}
                inviter={negotiation.inviter}
                members={negotiation.members}
                showBadge={true}
                status={displayStatus}
                isDisabled={
                  displayStatus == Status.Rejected ||
                  displayStatus == Status.Pending ||
                  displayStatus === Status.Terminated
                }
                isProfessor={true}
                advisorsCount={currentRoom?.advisors_count}
                pendingAdjudicationsCount={
                  currentRoom?.pending_adjudications_count
                }
                playersCount={currentRoom?.players_count}
              />
            )
          })}
        </div>
      ) : (
        <div className='h-[15.6rem] flex justify-center items-center flex-col gap-2 mt-11'>
          <img
            src='/icons/no-negotiation.svg'
            alt='No Sessions'
            className='w-40 h-40'
          />
          <p className='text-lg text-dark-blue'>No Negotiation Sessions</p>
        </div>
      )}
    </div>
  )
}

export default NegotiationSession
